import { useMemo } from 'react';
import type { EChartsType } from 'echarts/core';

import type { TimeSeriesChartProps } from '@/shared/charts/time-series-chart/TimeSeriesChart.types';
import type { TimeSeriesChartTheme } from '@/shared/charts/time-series-chart/theme/TimeSeriesChartTheme.types';

import { buildChartOption } from '../buildChartOption';
import type { LineViewport } from '../plot/level-of-detail/lineViewport';
import { usePlotWidth } from './usePlotWidth';
import { useVisibleRange } from './useVisibleRange';

type UseTimeSeriesChartOptionParams = Pick<
  TimeSeriesChartProps,
  'series' | 'rangeTrack' | 'timeDomain' | 'highlight' | 'navigation' | 'isTooltipEnabled' | 'sectionLabels'
> & {
  chart: EChartsType | undefined;
  theme: TimeSeriesChartTheme;
  renderTooltipContent: () => string;
};

/**
 * The ECharts option for the current props and viewport. The viewport — the settled visible window
 * and the plot width — is the only renderer-owned input; everything else comes from props.
 *
 * Memoised on purpose: building the lines walks the visible window of every series. Consumers keep
 * their series, range track and behaviour objects referentially stable while their contents are
 * unchanged, so the option is rebuilt only when something it depends on actually changed.
 *
 * Attach the returned ref to the element that sizes the chart.
 */
export function useTimeSeriesChartOption({
  chart,
  series,
  rangeTrack,
  timeDomain,
  highlight,
  navigation,
  isTooltipEnabled,
  sectionLabels,
  theme,
  renderTooltipContent,
}: UseTimeSeriesChartOptionParams) {
  const visibleRange = useVisibleRange({ chart, timeDomain });
  const { containerRef, plotWidthPx } = usePlotWidth();

  const viewport = useMemo<LineViewport>(() => ({ visibleRange, plotWidthPx }), [visibleRange, plotWidthPx]);

  const option = useMemo(
    () =>
      buildChartOption({
        series,
        rangeTrack,
        timeDomain,
        highlight,
        navigation,
        isTooltipEnabled,
        sectionLabels,
        theme,
        renderTooltipContent,
        viewport,
      }),
    [
      series,
      rangeTrack,
      timeDomain,
      highlight,
      navigation,
      isTooltipEnabled,
      sectionLabels,
      theme,
      renderTooltipContent,
      viewport,
    ]
  );

  return { option, containerRef };
}
